import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { hasStoredSession } from '@/lib/logto';

/**
 * Auth slice — holds the QLASH user record fetched from `GET /users/me`
 * plus a single source-of-truth `bridgeAuthenticated` flag for the Telegram
 * bridge flow. AppShell, Profile, and Settings all read from here so they
 * stay in sync after sign-in / sign-out.
 *
 * The access token itself is owned by `lib/logto.ts` (manual storage) or by
 * the Logto SDK (standalone web flow) — this slice does not track it.
 */

export interface AuthUser {
  id: number | string;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  email?: string;
  /**
   * QLASH admin flag from the Laravel `/users/me` payload. Drives the
   * DEV build access gate (`VITE_ADMIN_GATE_ENABLED=true`) alongside
   * `beta_tester`. Defaults to `false` when the backend omits the field so
   * non-admins are never accidentally elevated.
   */
  is_admin: boolean;
  /**
   * QLASH beta-tester flag from the Laravel `/users/me` payload. Grants
   * staging access (same as `is_admin`) in the DEV build, and is meant to
   * gate future beta-only features in PROD. Defaults to `false`.
   */
  beta_tester: boolean;
}

interface AuthState {
  user: AuthUser | null;
  /** Mirror of `hasStoredSession()` — drives the AppShell auth gate. True when
   * a fresh access token OR a redeemable refresh_token exists (persisted
   * session), so a returning user isn't bounced to Login on every reboot. */
  bridgeAuthenticated: boolean;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  bridgeAuthenticated: hasStoredSession(),
  status: 'idle',
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.status = 'idle';
      state.error = null;
    },
    setBridgeAuthenticated(state, action: PayloadAction<boolean>) {
      state.bridgeAuthenticated = action.payload;
    },
    setStatus(state, action: PayloadAction<AuthState['status']>) {
      state.status = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.status = action.payload ? 'error' : 'idle';
      state.error = action.payload;
    },
    logout(state) {
      state.user = null;
      state.bridgeAuthenticated = false;
      state.status = 'idle';
      state.error = null;
    },
  },
});

export const { setUser, setBridgeAuthenticated, setStatus, setError, logout } = authSlice.actions;
export default authSlice.reducer;
